import { useEffect, useState } from "react";

/**
 * Light-mode values of the `--finance-*` variables (index.css), used when the
 * stylesheet is not loaded (component tests).
 */
const FALLBACK_COLORS = {
  recurring: "#2a78d6",
  setup: "#eb6834",
  received: "#1baf7a",
  expenses: "#6250d6",
  overdue: "#e34948",
  other: "#8a8984",
  surface: "#ffffff",
  text: "#71717a",
  grid: "#e4e4e7",
};

export type FinanceColors = typeof FALLBACK_COLORS;

/** CSS variable read for each color; the last three are the theme surfaces */
const VARIABLES: Record<keyof FinanceColors, string> = {
  recurring: "--finance-recurring",
  setup: "--finance-setup",
  received: "--finance-received",
  expenses: "--finance-expenses",
  overdue: "--finance-overdue",
  other: "--finance-other",
  surface: "--card",
  text: "--muted-foreground",
  grid: "--border",
};

const readFinanceColors = (): FinanceColors => {
  const style = getComputedStyle(document.documentElement);
  return Object.fromEntries(
    Object.entries(VARIABLES).map(([key, variable]) => [
      key,
      style.getPropertyValue(variable).trim() ||
        FALLBACK_COLORS[key as keyof FinanceColors],
    ]),
  ) as FinanceColors;
};

/**
 * Finance chart colors as concrete values. nivo animates colors with
 * react-spring, which cannot interpolate a `var(--x)` string, so the variables
 * are resolved here and read again when the theme class of <html> changes.
 */
export const useFinanceColors = (): FinanceColors => {
  const [colors, setColors] = useState(readFinanceColors);
  useEffect(() => {
    const update = () => setColors(readFinanceColors());
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    update();
    return () => observer.disconnect();
  }, []);
  return colors;
};
